import React, { useEffect, useRef, useState } from 'react';
import { Button, Header, Input, Pagination, Modal, Dropdown } from 'react-frontier';
import { Table } from 'react-frontier';
import { APIResponse, SetLoading } from '../SiteClasses';

interface SearchModalProps<T=any> extends SearchCoreProps<T>{
	searchData: (query: string, page: number, page_size: number)=>Promise<APIResponse<T[]>>,
	onShowLoad?: (query: string, page: number, page_size: number)=>Promise<APIResponse<T[]>>,
	table: {
		name: any,
		key?: keyof T,
		collapsing?: boolean,
		centered?: boolean,
		render?: (val: any, obj: T)=>any
	}[],
}

export interface SearchCoreProps<T>{
	open: boolean,
	onClose: ()=>void,
	size?: 'small' | 'tiny' | 'mini',
	title?: string,
	pageSize?: number,
	searchPlaceholder?: string,
	onSelected: (item: T, setLoading: SetLoading)=>void,
	showFilterButton?: boolean,
	filterOptions?: { text: string, value: any }[],
	onFilterChange?: (value: any)=>void,
	selectedFilter?: any,
}

var SearchCoreModal = (props: SearchModalProps)=>{
	var [searching, setSearching] = useState<boolean>(false);
	var [searchInput, setSearchInput] = useState<string>('');
	var [results, setResults] = useState<any[]>(null);
	var [resultsError, setResultsError] = useState<string>(null);
	var [searchResults, setSearchResults] = useState<any[]>(null);
	var [searchError, setSearchError] = useState<string>(null);
	var [sentQuery, setSentQuery] = useState<string>(null);
	var [page, setPage] = useState<number>(0);
	var [loading, setLoading] = useState<boolean>(false);
	var inputRef = useRef<HTMLInputElement>(null);
	var PAGE_SIZE = (props.pageSize || 15);

	useEffect(()=>{
		if(props.open){
			setSearchInput('');
			inputRef.current.focus();
			setLoading(false);
			if(props.onShowLoad){
				setSearching(true);
				setResultsError(null);
				setSearchError(null);
				setSentQuery(null);
				props.onShowLoad(null, 0, PAGE_SIZE).then(res=>{
					if(res.error) return setResultsError(res.message);
					setResults(res.data && res.data.length==0 ? null : res.data);
				}).catch(err=>{
					setResults(null);
					
				}).finally(()=>{
					setSearching(false);
				})
			}else{
				setSearching(false);
			}
		}
	}, [props.open]);

	useEffect(() => {
		if (props.open && props.selectedFilter !== undefined) {
			
			if (sentQuery) {
				
				searchData(null, 0);
			} else if (props.onShowLoad) {
				
				setSearching(true);
				setResultsError(null);
				setSearchError(null);
				setSearchResults(null);
				props.onShowLoad(null, 0, PAGE_SIZE).then(res => {
					if (res.error) return setResultsError(res.message);
					setResults(res.data && res.data.length==0 ? null : res.data);
				}).catch(err => {
					setResults(null);
				}).finally(() => {
					setSearching(false);
				});
			}
		}
	}, [props.selectedFilter]);

	var getIndexes = (arr: boolean[])=>{
		var ix : number[] = [];
		for(var i=0; i<arr.length; i++){
			if(!!arr[i]) ix.push(i);
		}
		return ix;
	}

	var searchData = (setLoading: SetLoading, page?: number)=>{
		if(searching) return;
		setSearching(true);
		setSentQuery(searchInput);
		var currentPage = page | 0;
		setPage(currentPage);
		setSearchError(null);
		setResultsError(null);
		if(setLoading) setLoading(true);
		props.searchData(searchInput, currentPage, PAGE_SIZE).then(res=>{
			if(res.error) return setSearchError(res.message);
			setSearchResults(res.data);
		}).catch(err=>{
			setSearchError('Hubo un error inesperado cargando los datos (LCL-SCHCRE-1)');
		}).finally(()=>{
			setSearching(false);
			if(setLoading) setLoading(false);
		})
	}

	var itemSelected = (item: any)=>{
		return ()=>{
			if(props.onSelected){
				props.onSelected(item, setLoading);
			}
		}
	}

	var onPageChange = (page: number)=>{
		setPage(page);
		searchData(null, page);
	}

	var hasPagination = (searchResults?.length || 0) >= PAGE_SIZE || page != 0;
	var shown_results = (searchResults || results);

	return <Modal open={props.open} onClose={props.onClose} size={props.size || 'tiny'}>
		<Modal.Header>
			{props.title}
		</Modal.Header>
		<Modal.Content style={{ paddingBottom: hasPagination ? 0 : 20, overflow: 'hidden' }}>
			{!!props.open && (
				<>
					<Input 
						ref={inputRef} 
						value={searchInput} 
						onChange={setSearchInput} 
						placeholder={props.searchPlaceholder || 'Buscar registros'} 
						inputType='text' 
						button={<Button text='Buscar' onClick={searchData} loading={searching} />}
						onSubmit={searchData} 
						submitOnEnter 
					/>
					{props.showFilterButton && (
						<Dropdown
							iconName='filter'
							value={props.selectedFilter}
							onValueChange={props.onFilterChange}
							items={[
								{ text: "Filtrar", value: null },
								...(props.filterOptions || [])
							]}
							style={{ marginTop: 10 }}
							menuStyle={{ maxHeight: '300px', overflowY: 'auto' }}
						/>
					)}
				</>
			)}
			{searching ? (
				<Header loading text={`Cargando registros`} containerStyle={{ marginBottom: 50, marginTop: 50 }} />
			) : (searchError || resultsError) ? (
				<Header text='Error' subtext={searchError || resultsError} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
			) : !shown_results || shown_results.length === 0 ? (
				page <= 0 ? (
					<Header text='Sin resultados' subtext={'No se encontraron registros'+(sentQuery ? ` con "${sentQuery}"` : '')} iconName='face-frown-open' style={{ marginTop: 50, marginBottom: 30 }} size='small' />
				) : null
			) : <>
				<div style={{ position: 'relative' }}>
					<Table fitted selectable striped divided
						style={{ marginTop: 20 }}
						className='first bottom round'
						headers={[
							...props.table.map(a=>a.name)
						]}
						centeredIndexes={getIndexes([
							...props.table.map(a=>a.centered)
						])}
						collapsingIndexes={getIndexes([
							...props.table.map(a=>a.collapsing)
						])}
						footer={hasPagination && (
							<Table.Cell row colSpan={100}>
								<div style={{ justifyContent: 'flex-end', display: 'flex', marginBottom: 15 }}>
									<Pagination page={page} disabled={searching} onPageChange={onPageChange} hasNext={shown_results.length >= PAGE_SIZE}/>
								</div>
							</Table.Cell>
						)}
					>
						{(shown_results.length === 0) ? (
							<Table.Cell row colSpan={100}>
								<Header text='Sin resultados' subtext={`Esta página no tiene resultados`} iconName='face-frown-open' style={{ marginTop: 30, marginBottom: 30 }} size='small' />
							</Table.Cell>
						) : (
							shown_results.map((a, i)=>{
								var rowData = [
									...props.table.map(t => t.render ? t.render(a[t.key], a) : a[t.key])
								];
								return (
									<Table.Row
										key={`SRCH-CRTR-${i}`}
										data={rowData}
										onClick={itemSelected(a)}
									/>
								);
							})
						)}
					</Table>
				</div>
			</>}
			{loading && (
				<div style={{ position: 'absolute', width: '100%', left: 0, top: 0, height: '100%', backgroundColor: 'rgba(0, 0, 0, 0.5)', borderRadius: 8 }} className='fr big loading'></div>
			)}
		</Modal.Content>
	</Modal>
}

export default SearchCoreModal;